// Offline image handling functionality
import { getImageFromDB } from './indexedDB';

// Track blob URLs for cleanup
const blobUrlRegistry = new Map();

// Utility function to load images with offline support
function loadImageWithOfflineSupport(imgElement, imagePath) {
    if (!imagePath) return;
    
    // Normalize path - remove leading ../ and ensure it starts with /
    let normalizedPath = imagePath.replace(/^(\.\.\/)+/, '');
    if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('data:') && !normalizedPath.startsWith('blob:')) {
        normalizedPath = '/' + normalizedPath;
    }
    
    if (normalizedPath !== imagePath) {
        // console.log('Normalized path:', imagePath, '->', normalizedPath);
    }
    
    // Set a loading placeholder or keep existing src as fallback
    const currentSrc = imgElement.src || '';
    if (!currentSrc) {
        imgElement.style.opacity = '0.5'; // Visual indication of loading
    }
    
    getImageFromDB(normalizedPath).then(imageBlob => {
        if (imageBlob) {
            // console.log('Image blob found, rendering from blob:', normalizedPath);
            const blobUrl = URL.createObjectURL(imageBlob);
            
            // Store in registry for cleanup
            blobUrlRegistry.set(imgElement, blobUrl);
            
            // Set the image source to the blob URL
            imgElement.src = blobUrl;
            imgElement.style.opacity = '1';
            
            // Set up cleanup when image is removed from DOM
            const observer = new MutationObserver((mutations) => {
                if (!document.contains(imgElement)) {
                    // console.log('Image removed from DOM, cleaning up blob URL:', normalizedPath);
                    URL.revokeObjectURL(blobUrl);
                    blobUrlRegistry.delete(imgElement);
                    observer.disconnect();
                }
            });
            
            observer.observe(document.body, { childList: true, subtree: true });
        } else {
            // console.log('Image blob not found, falling back to network path:', normalizedPath);
            imgElement.src = normalizedPath;
            imgElement.style.opacity = '1';
        }
    }).catch(error => {
        // console.error('Error retrieving image from IndexedDB:', error);
        imgElement.src = normalizedPath;
        imgElement.style.opacity = '1';
    });
}

// Initialize all images on a page with offline support
function initializeOfflineImages() {
    // Handle images with src attribute
    document.querySelectorAll('img[src]').forEach(img => {
        const imagePath = img.getAttribute('src');
        if (imagePath && !imagePath.startsWith('data:') && !imagePath.startsWith('blob:')) {
            // Store current src for fallback
            const originalSrc = img.src;
            // Clear src temporarily to prevent double loading
            img.removeAttribute('src');
            
            // Load with offline support
            loadImageWithOfflineSupport(img, imagePath);
        }
    });
    
    // Handle images with data-src attribute (lazy loading)
    // Uncomment if you need to handle lazy-loaded images

    document.querySelectorAll('img[data-src]').forEach(img => {
        const imagePath = img.getAttribute('data-src');
        if (imagePath && !imagePath.startsWith('data:') && !imagePath.startsWith('blob:')) {
            loadImageWithOfflineSupport(img, imagePath);
        }
    });
    
    // Handle background images in CSS
    // Uncomment if you need to handle background images
    /*
    document.querySelectorAll('[data-bg-src]').forEach(el => {
        const imagePath = el.getAttribute('data-bg-src');
        if (imagePath) {
            getImageFromDB(imagePath).then(imageBlob => {
                if (imageBlob) {
                    const blobUrl = URL.createObjectURL(imageBlob);
                    blobUrlRegistry.set(el, blobUrl);
                    el.style.backgroundImage = `url('${blobUrl}')`;
                    
                    // Set up cleanup
                    const observer = new MutationObserver((mutations) => {
                        if (!document.contains(el)) {
                            URL.revokeObjectURL(blobUrl);
                            blobUrlRegistry.delete(el);
                            observer.disconnect();
                        }
                    });
                    
                    observer.observe(document.body, { childList: true, subtree: true });
                } else {
                    el.style.backgroundImage = `url('${imagePath}')`;
                }
            }).catch(error => {
                console.error('Error retrieving background image from IndexedDB:', error);
                el.style.backgroundImage = `url('${imagePath}')`;
            });
        }
    });
    */
}

// Cleanup function for page transitions
function cleanupBlobUrls() {
    blobUrlRegistry.forEach((blobUrl, element) => {
        if (!document.contains(element)) {
            // console.log('Cleaning up orphaned blob URL');
            URL.revokeObjectURL(blobUrl);
            blobUrlRegistry.delete(element);
        }
    });
}

export {
    loadImageWithOfflineSupport,
    initializeOfflineImages,
    cleanupBlobUrls
};
